import { OfferSubCategory } from '../../common/enums.js';
import { eurosToCents } from '../../common/utils/priceUtils.js';
import {
  getGroupOfferingPriceInEuros,
  getRecurringOfferingPriceInEuros,
  isOfferingInStock,
} from './ProductOfferingsModalCoCoUtils.js';
import type { ProductOfferingsModalCoCoConfig } from './ProductOfferingsModalCoCo.js';
import type { BundleAddonGroup } from '../../generated/api/bundleAddonGroup.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { BundledAddonGroupSelection } from '../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';

type BundleAddonGroupResponse = NonNullable<ProductOfferingsResponse['bundleAddonGroup']>[number];
type FixedBroadbandPriceContext = Parameters<NonNullable<ProductOfferingsModalCoCoConfig['calculatePrices']>>[0];

export const initializeFixedBroadbandGroupSelection = (group: BundleAddonGroupResponse): BundledAddonGroupSelection => {
  const isSingleSelect = group.cardinality?.maxNum === 1;
  const offerings = (group.productOffering ?? []).filter(isOfferingInStock);

  if (isSingleSelect) {
    const defaultOffering = offerings.find(o => o.isDefault === true);
    const fallbackOfferingIds = offerings.length > 0 ? [offerings[0].id] : [];
    return {
      groupId: group.id,
      selectedOfferingIds: defaultOffering ? [defaultOffering.id] : fallbackOfferingIds,
    };
  }

  return {
    groupId: group.id,
    selectedOfferingIds: offerings.filter(o => o.isDefault === true).map(o => o.id),
  };
};

export const calculateFixedBroadbandPrices = ({
  bundledOfferings,
  bundleAddonGroups,
  groupSelections,
  recurringCharge,
  quantity,
  oneTimeCharge,
}: FixedBroadbandPriceContext): ReturnType<NonNullable<ProductOfferingsModalCoCoConfig['calculatePrices']>> => {
  const totalAddOnPrice = bundledOfferings.reduce(
    (total, offering) =>
      offering.isChecked
        ? total + eurosToCents(getRecurringOfferingPriceInEuros(offering.productOfferingPrice))
        : total,
    0
  );
  const totalEquipmentOcPrice = groupSelections.reduce((total, selection) => {
    const group = bundleAddonGroups.find(g => g.id === selection.groupId);
    return (
      total +
      (group?.productOffering ?? [])
        .filter(offering => selection.selectedOfferingIds.includes(offering.id))
        .reduce(
          (groupTotal, offering) =>
            groupTotal + eurosToCents(getGroupOfferingPriceInEuros(offering.productOfferingPrice)),
          0
        )
    );
  }, 0);

  return {
    recurringPriceInCents: (totalAddOnPrice + recurringCharge) * quantity,
    oneTimePriceInCents: oneTimeCharge + totalEquipmentOcPrice,
  };
};

export const isInstallationServiceGroup = (group: BundleAddonGroup | CartBundleAddonGroup): boolean =>
  (group.productOffering?.length ?? 0) > 0 &&
  group.productOffering!.every(
    offering => offering.productOfferingType?.subcategory === OfferSubCategory.INSTALLATION_SERVICE_POT
  );
